import { EllipsoidGeodesic, Cartographic } from 'cesium';

export type LonLat = readonly [number, number];

/**
 * Resample a lon/lat polyline at a fixed arc-length spacing (metres) using
 * geodesic interpolation. Each output station becomes a candidate sleeper
 * position when spacing === sleeper pitch. Always includes the final vertex.
 */
export function resampleByArcLength(line: ReadonlyArray<LonLat>, spacing: number): LonLat[] {
  if (line.length < 2 || spacing <= 0) return line.slice();

  const out: LonLat[] = [line[0]];
  let carry = 0; // distance already consumed into the current segment

  const geo = new EllipsoidGeodesic();
  const start = new Cartographic();
  const end = new Cartographic();

  for (let i = 0; i < line.length - 1; i++) {
    Cartographic.fromDegrees(line[i][0], line[i][1], 0, start);
    Cartographic.fromDegrees(line[i + 1][0], line[i + 1][1], 0, end);
    geo.setEndPoints(start, end);
    const segLen = geo.surfaceDistance;
    if (segLen === 0) continue;

    let dist = spacing - carry;
    while (dist < segLen) {
      const c = geo.interpolateUsingSurfaceDistance(dist);
      out.push([radToDeg(c.longitude), radToDeg(c.latitude)]);
      dist += spacing;
    }
    carry = segLen - (dist - spacing);
  }

  const last = line[line.length - 1];
  const prev = out[out.length - 1];
  if (prev[0] !== last[0] || prev[1] !== last[1]) out.push(last);
  return out;
}

function radToDeg(r: number): number {
  return (r * 180) / Math.PI;
}

/**
 * Smooth a height profile with a centred moving average so the rail follows a
 * continuous grade rather than per-pixel LiDAR noise. `window` is the half-width
 * in samples.
 */
export function smoothHeights(heights: ReadonlyArray<number>, window = 4): number[] {
  const n = heights.length;
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    let sum = 0;
    let count = 0;
    for (let j = Math.max(0, i - window); j <= Math.min(n - 1, i + window); j++) {
      sum += heights[j];
      count++;
    }
    out[i] = sum / count;
  }
  return out;
}
